import identity from 'lodash/identity';
import pickBy from 'lodash/pickBy';
import moment from 'moment';

import { api } from '$app/api';
import { poundsToPence, penceToPounds } from '$app/utils/util';

const removeFalsey = (value) => pickBy(value, identity);

const withDistanceBasedPricingValue = (hero) => ({
  ...hero,
  distanceBasedPricing: hero.distanceBasedPricing ? 'true' : 'false',
});

const getDefaultRateValue = (rates = []) => {
  const defaultRate = rates.find((rate) => rate.rateType === 'default');
  return defaultRate ? penceToPounds(defaultRate.pence) : '-';
};

export const getHeroesErrorMessage = (error) =>
  error?.response?.data?.message || error?.message || 'Something went wrong';

export const createDefaultRate = (values) => ({
  pence: poundsToPence(values.defaultRate),
  rateType: 'default',
});

export const createHeroPayload = (values) => ({
  ...values,
  rates: [createDefaultRate(values)],
});

export const normalizeHeroFormValues = (hero) => {
  const heroWithDbp = withDistanceBasedPricingValue(hero);

  return {
    ...heroWithDbp,
    expiryDate: heroWithDbp.expiryDate ? moment(heroWithDbp.expiryDate) : null,
    issueDate: heroWithDbp.issueDate ? moment(heroWithDbp.issueDate) : null,
    dateOfBirth: heroWithDbp.dateOfBirth ? moment(heroWithDbp.dateOfBirth) : null,
    staffNumber: heroWithDbp.staffNumber ? `${heroWithDbp.staffNumber}` : null,
    defaultRate: heroWithDbp.rates ? getDefaultRateValue(heroWithDbp.rates) : '-',
    driverStartDate: heroWithDbp.driverStartDate ? moment(heroWithDbp.driverStartDate) : null,
    driverEndDate: heroWithDbp.driverEndDate ? moment(heroWithDbp.driverEndDate) : null,
  };
};

export async function fetchDriverZones() {
  const { data } = await api('/zones/areas/all?type=driver');
  return data;
}

export async function fetchHeroes() {
  const [allHeroesResponse, zonesResponse] = await Promise.all([
    api('/drivers?all=true', {
      method: 'get',
      data: {},
    }),
    api('/zones/areas/all?type=driver'),
  ]);

  return allHeroesResponse.data.map((hero) => {
    const zone = zonesResponse.data.find((candidate) => candidate.ownerId === hero.defaultZoneId);

    return {
      ...hero,
      zoneName: (zone && zone.name) || 'Not assigned',
    };
  });
}

export async function createHero(values) {
  const payload = createHeroPayload(values);
  const { data } = await api('/drivers', {
    method: 'post',
    data: {
      ...payload,
      distanceBasedPricing: payload.distanceBasedPricing === 'true',
    },
  });

  return data;
}

export async function fetchHero(heroId) {
  const { data } = await api(`/drivers/${heroId}`);
  return normalizeHeroFormValues(data);
}

export async function updateHero(heroId, values, existingRates = []) {
  const oldRate = existingRates.find((rate) => rate.rateType === 'default');
  const pence = poundsToPence(values.defaultRate);
  const rates = [
    oldRate
      ? { ...oldRate, pence }
      : { pence, rateType: 'default', picker: values.heroId },
  ];

  const payload = {
    ...values,
    rates,
  };

  const { data } = await api(`/drivers/${heroId}`, {
    method: 'patch',
    data: {
      ...removeFalsey(payload),
      distanceBasedPricing: values.distanceBasedPricing === 'true',
    },
  });

  return normalizeHeroFormValues(data);
}

export async function fetchHeroAddresses(driverUuid) {
  const { data } = await api(`/drivers/${driverUuid}/addresses`);
  return data;
}

export async function saveHeroAddress(heroId, address) {
  const { data } = await api(`/drivers/${heroId}/addresses`, {
    method: 'post',
    data: address,
  });

  return data;
}
